import { format } from 'date-fns'
import { Link2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { FieldError } from '@/components/ui/field'
import {
  createCustomerLink,
  type CustomerLink,
  type CustomerLinkAvailability,
  fillApplicationErrorMessage,
} from '@/models/fillApplication'
import { FillApplicationEvent, trackFillApplication } from './fillApplicationAnalytics'
import { LinkField } from './LinkField'

interface CustomerSignInLinkProps {
  orgId: string
  link: CustomerLinkAvailability
}

/** Makes the customer's one-click sign-in link in place. */
export function CustomerSignInLink({ orgId, link }: CustomerSignInLinkProps) {
  const [made, setMade] = useState<CustomerLink | null>(null)
  const [making, setMaking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function make() {
    setMaking(true)
    setError(null)
    try {
      setMade(await createCustomerLink(orgId))
    } catch (makeError) {
      // NOT_SENT, LINK_TARGET_REFUSED and the hourly limit (a plain 403) each say what to do.
      setError(fillApplicationErrorMessage(makeError))
    } finally {
      setMaking(false)
    }
  }

  if (made) {
    return (
      <div className="flex flex-col gap-2">
        <LinkField
          url={made.url}
          label="Customer link"
          onCopied={() => trackFillApplication(FillApplicationEvent.CUSTOMER_LINK_COPIED, { org_id: orgId })}
        />
        <span className="text-ink-500 text-xs">
          Signs in as {made.user.name} ({made.user.email}) until {format(new Date(made.expiresAt), 'MMM d')}.
          Anyone with the link can use it, so don&apos;t open it yourself.
        </span>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <Button
        type="button"
        variant="outline"
        onClick={() => void make()}
        disabled={!link.available}
        loading={making ? 'Making the link…' : undefined}
      >
        <Link2 /> Generate sign-in link
      </Button>
      {!link.available && link.message && <span className="text-ink-500 text-xs">{link.message}</span>}
      <FieldError className="text-xs">{error}</FieldError>
    </div>
  )
}
